

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { colors2, getVal, UI_CAPTION } from "oolib";
import { StickyHorizontalScrollbar } from "../../generalUI/StickyHorizontalScrollbar";
import TableHeaderElem from "./comps/TableHeaderElem";
import TableRowElem from "./comps/TableRowElem";
import { TableSkeletonLoader } from "./comps/TableSkeletonLoader";
import { useParseTplToTableConfig } from "./CONFIG_PARSER/useParseTplToTableConfig";
import { StyledDataTableRowsContainer, StyledTableFrame } from "./styled";
import { filterOutHiddenCols } from "./utils/filterOutHiddenCols";
import { sortCellDataAsPerOrderConfig } from "./utils/sortCellDataAsPerOrderConfig";
import { useHandleTableColWidths } from "./utils/useHandleTableColWidths";
import { useTableSort } from "./utils/tableSortUtils/useTableSort";
import { ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import { UI_BODY_SM_DF } from "oolib/dist/v2/components/Typo2";
import { useGetBulkActionsConfig } from "./utils/useGetBulkActionsConfig";
import { useTableRowDelete } from "./utils/useTableRowDelete";
import { useTableRowVirtualizer } from "./utils/useTableRowVirtualizer";
import { useKeyDown } from "../../../utils/customHooks/useKeyDown";

export const ROW_HEIGHT = 36;

function TableElem({
  id,
  status,
  data: dataProp = [],

  tableConfig: tableConfigProp,
  actionsConfig,
  conditionsConfig,
  handleRowClick,
  TableWrapper = "div",

  lazyLoad: {
    observerRef,
    isFetchingNextPage,
    backendActiveSort,
    setBackendActiveSort,
  } = {},

  //builder props
  builderProps,

  handleCellChange,
  //for future
  addRow,
  enableContainerQuery,
  customTopComp,
  headerEditable = false,
  headerValidationErrors = {},
  onHeaderNameUpdate,
  validateSingleHeader,
  enableContrastHeaderStyle = false,
  withStickyHeader, // for directdataimport modal header
  stickyHeaderTopOffset, // px height of customTopComp, so the sticky header parks below it
  disableRowStripes = false,
  // Mode-2 (parent-driven) bulk actions: parent supplies the handler map
  // and renders its own modals at page level. When omitted, we fall back
  // to the internal useGetBulkActionsConfig (mode-1) — same shape, baked
  // for content-doc tables (listing page, self-serve survey, etc.).
  bulkActionsConfig: bulkActionsConfigProp,
  // Optional controlled selection: pass both to let the page read/drive the
  // selected rows (e.g. a page-level toolbar). Omit to keep it internal.
  selectedRowIds: selectedRowIdsProp,
  setSelectedRowIds: setSelectedRowIdsProp,
  // Page renders its own bulk-action UI; skip the built-in button row.
  hideBulkActionsBar = false,
  // One-line message shown inside the table when it has no rows.
  emptyStateText,
  /**
   * ⚠️ CUSTOM — for ONE specific use case ONLY: the self-managed listing page's
   * per-column "change field type" header control. NOT a general-purpose
   * TableElem prop. Do NOT reach for it for anything else, and do NOT grow it
   * into a generic hook for injecting header controls — if you need something
   * like this elsewhere, design a proper API rather than widening this.
   *
   * The `custom_` prefix and the use-case in the name are deliberate: they make
   * it obvious at every call site that this is a one-off escape hatch leaking a
   * listing-page concern through a shared component.
   *
   * Shape: { contentType }. Set only by ListingPage's TableStyle, and only when
   * SELF_MANAGED_BASE_CONFIGS is present. Undefined everywhere else, which is
   * what keeps the control off every other table. Passed straight through to
   * TableHeaderElem.
   */
  custom_listingPageChangeFieldCompProps,
}) {
  // const props = arguments[0]
  // useTraceUpdateOnLocal(props);

  const { tableConfig } = useParseTplToTableConfig({
    tableConfigProp,
    isBackendSort: !!backendActiveSort,
  });

  /**
   * We feel that it might be more 'correct' to have the sidebar controlling hide/show & column order,
   * within the table comp itself (rather than outsourcing to ContentTypeConfigDashboard).
   *
   * If we do make that change, then the below useEffect will become irrelevant
   * this is a bit problematic, cuz it led to infinite loop
   * when sortCellData was done in useParseTplToTable...
   */
  useEffect(() => {
    builderProps?.trackParsedTableConfig &&
      builderProps.trackParsedTableConfig(
        sortCellDataAsPerOrderConfig(tableConfig)
      );
  }, [tableConfig]);

  /**
   * having this as a state here can be used for
   * giving user control over col widths in the future
   */
  const { colWidthConfig, setColWidthConfig, minColWidth } = useHandleTableColWidths({
    tableConfig,
  });

  //sort related stuff
  const { data, activeSorts, handleTableSort } = useTableSort({
    tableConfig,
    dataProp,
    backendActiveSort,
    setBackendActiveSort,
  });

  const {
    bulkActions_selectedRowIds: internalSelectedRowIds,
    bulkActions_setSelectedRowIds: internalSetSelectedRowIds,
    bulkActionsConfig: internalBulkActionsConfig,
    ActivateSelfServeSurveyModal,
  } = useGetBulkActionsConfig({
    contentType: data?.[0]?.meta?.kp_content_type,
    tableConfig,
  });
  // Parent-driven config wins. Selection state stays internal regardless;
  // parent handlers receive { data, selectedRowIds, clearSelection } at
  // click time so they don't need a closure on it.
  const bulkActionsConfig = bulkActionsConfigProp || internalBulkActionsConfig;
  const isSelectionControlled = !!selectedRowIdsProp && !!setSelectedRowIdsProp;
  const bulkActions_selectedRowIds = isSelectionControlled ? selectedRowIdsProp : internalSelectedRowIds;
  const bulkActions_setSelectedRowIds = isSelectionControlled ? setSelectedRowIdsProp : internalSetSelectedRowIds;
  const clearBulkSelection = () => bulkActions_setSelectedRowIds([]);

  const processedTableConfig = useMemo(
    () => filterOutHiddenCols(sortCellDataAsPerOrderConfig(tableConfig)),
    [tableConfig]
  );

  const { requestRowDelete, deleteModal } = useTableRowDelete();

  const rowHeight = processedTableConfig.rowHeight || ROW_HEIGHT;
  // expanded rows have variable height, which fixed-size virtualization
  // can't represent — those tables are ineligible. The hook applies a
  // further row-count gate on top (small tables render every row, no flash)
  // and reports back whether it actually virtualized via isVirtualizing.
  const virtualizationEligible = !processedTableConfig.expandComp;

  const { rowsContainerRef, virtualRows, totalHeight, isVirtualizing } =
    useTableRowVirtualizer({
      data,
      rowHeight,
      enabled: virtualizationEligible,
    });

  /**
   * we need to do this tableRowIds setting here
   * just 1 single time in the components lifecycle
   * because, if the idpath field happens to be editable,
   * it can lead to all sorts of strange bugs if the row id
   * keeps changing during edit.
   */
  const tableRowIds = useMemo(() => {
    if (status === undefined || status === "success") {
      return data.map((d) => {
        return getVal(d, tableConfig.rowIdPath);
      });
    }
  }, [status, data.length]);

  // Drop selected ids that are no longer in the table. Skipped when the
  // parent owns the selection: it may filter rows (e.g. a search) and keep
  // the hidden ones selected, so it prunes against its full data itself.
  useEffect(() => {
    if (isSelectionControlled || !tableRowIds) return;
    bulkActions_setSelectedRowIds((prevSelected) => {
      const kept = prevSelected.filter((id) => tableRowIds.includes(id));
      return kept.length === prevSelected.length ? prevSelected : kept;
    });
  }, [tableRowIds]);

  // Drag across row checkboxes to multiselect, like components/UI/List:
  // mousedown toggles that row and fixes the action (select/deselect);
  // entering another row's checkbox applies it to every row since the last
  // one entered, so a fast drag that skips rows misses none.
  const dragSelectRef = useRef(null); // { select, lastIdx } while dragging
  const selectRowRange = useCallback(
    (fromIdx, toIdx, select) => {
      const ids = tableRowIds
        .slice(Math.min(fromIdx, toIdx), Math.max(fromIdx, toIdx) + 1)
        .filter((id) => !processedTableConfig.disabledRowIds?.includes(id));
      bulkActions_setSelectedRowIds((prev) =>
        select ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))
      );
    },
    [tableRowIds, processedTableConfig.disabledRowIds]
  );
  const handleCheckboxDragStart = useCallback(
    (rowIdx, isSelected) => {
      dragSelectRef.current = { select: !isSelected, lastIdx: rowIdx };
      selectRowRange(rowIdx, rowIdx, !isSelected);
    },
    [selectRowRange]
  );
  const handleCheckboxDragOver = useCallback(
    (rowIdx) => {
      const drag = dragSelectRef.current;
      if (!drag) return;
      selectRowRange(drag.lastIdx, rowIdx, drag.select);
      drag.lastIdx = rowIdx;
    },
    [selectRowRange]
  );
  useEffect(() => {
    const endDrag = () => (dragSelectRef.current = null);
    document.addEventListener("mouseup", endDrag);
    return () => document.removeEventListener("mouseup", endDrag);
  }, []);

  const bulkActions = tableConfig.bulkActions;

  /**
   * Delete (Windows) / Backspace (mac) is the same action as the bulk Delete
   * button, so it's dispatched through the same handler — including its confirm
   * modal. Bound only while a usable bulkDelete action AND a selection exist,
   * which keeps it off every table that doesn't offer bulk delete.
   */
  const keyboardBulkDeleteEnabled =
    bulkActions_selectedRowIds.length > 0 &&
    !!bulkActionsConfig?.bulkDelete &&
    bulkActions?.some(
      (actionObj) =>
        actionObj.action === "bulkDelete" && actionObj.disabled !== true
    );

  useKeyDown(
    ["Delete", "Backspace"],
    (e) => {
      // Backspace would otherwise navigate back in some browsers
      e.preventDefault();
      bulkActionsConfig.bulkDelete({
        data,
        selectedRowIds: bulkActions_selectedRowIds,
        clearSelection: clearBulkSelection,
      });
    },
    { enabled: keyboardBulkDeleteEnabled }
  );

  // tableConfig.frameOutsideScroll draws the border around the scroll
  // viewport (see StyledTableFrame); otherwise there is no wrapper at all.
  const Frame = processedTableConfig.frameOutsideScroll ? StyledTableFrame : React.Fragment;

  return (
    <Frame>
    <StickyHorizontalScrollbar>
      <div style={{ width: "fit-content" }}>
        <TableWrapper enableContainerQuery={enableContainerQuery}>
          {customTopComp && typeof customTopComp === "function"
            ? customTopComp({ colWidthConfig, setColWidthConfig })
            : customTopComp}
          {ActivateSelfServeSurveyModal}
          {deleteModal}
          {bulkActions && !hideBulkActionsBar && (
            <div
              style={{
                paddingTop: "1px",
                paddingBottom: "6px",
                paddingLeft: "1px",
                display: "flex",
                gap: "4px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {bulkActions.map((actionObj) => (
                <ButtonTertiaryCompact
                  color={colors2.grey80}
                  disabled={
                    bulkActions_selectedRowIds.length === 0 ||
                    actionObj.disabled === true
                  }
                  onClick={() =>
                    bulkActionsConfig[actionObj.action]({
                      data,
                      selectedRowIds: bulkActions_selectedRowIds,
                      clearSelection: clearBulkSelection,
                    })
                  }
                >
                  {actionObj.display}
                </ButtonTertiaryCompact>
              ))}
              {/* "Select All" only ticks rows that have been loaded so far —
                  the table lazy-loads on scroll. Surface this once the user
                  has actually selected anything so they aren't surprised by
                  the count in the next confirm modal / banner. */}
              {bulkActions_selectedRowIds.length > 0 && (
                <UI_CAPTION
                  color="greyColor60"
                  style={{ marginLeft: "8px" }}
                >
                  {bulkActions_selectedRowIds.length} selected · only currently loaded rows
                </UI_CAPTION>
              )}
            </div>
          )}
          <TableHeaderElem
            id={id + "_header"}
            tableConfig={processedTableConfig}
            headerEditable={headerEditable}
            handleTableSort={handleTableSort}
            activeSorts={activeSorts}
            actions={tableConfig.actions}
            bulkActions={tableConfig.bulkActions}
            colWidthConfig={colWidthConfig}
            setColWidthConfig={setColWidthConfig}
            backendActiveSort={backendActiveSort}
            builderProps={builderProps}
            tableRowIds={tableRowIds}
            // primitive count of currently rendered rows — used by the
            // bulkActions select-all checkbox to drive its mixture state.
            // tableRowIds.length lags by a render after lazy-load growth
            // (it's a useMemo with a setState side effect), data.length
            // doesn't.
            loadedRowsCount={data.length}
            bulkActions_selectedRowIds={bulkActions_selectedRowIds}
            bulkActions_setSelectedRowIds={bulkActions_setSelectedRowIds}
            headerValidationErrors={headerValidationErrors}
            onHeaderNameUpdate={onHeaderNameUpdate}
            validateSingleHeader={validateSingleHeader}
            enableContrastHeaderStyle={enableContrastHeaderStyle}
            withStickyHeader={withStickyHeader}
            stickyHeaderTopOffset={stickyHeaderTopOffset}
            minColWidth={minColWidth}
            custom_listingPageChangeFieldCompProps={custom_listingPageChangeFieldCompProps}
          />
          <StyledDataTableRowsContainer $frameOutsideScroll={processedTableConfig.frameOutsideScroll}>
            {status !== undefined && status === "loading" ? (
              <TableSkeletonLoader rowsCount={10} />
            ) : (
              <>
                {/* fixed-height relative container + absolutely positioned
                    rows at index * rowHeight: nothing about the container
                    changes during scroll (zero relayout) — rows just mount/
                    unmount at the edges of the visible range. Deliberately
                    NOT a translateY wrapper: an ancestor transform breaks
                    the frozen first column's position:sticky in Chromium. */}
                <div
                  ref={rowsContainerRef}
                  style={{
                    position: "relative",
                    height: totalHeight != null ? totalHeight + "px" : undefined,
                  }}
                >
                  {virtualRows.map((virtualRow) => {
                    const i = virtualRow.index;
                    const d = data[i];
                    const rowId = tableRowIds[i];
                    return (
                      <div
                        key={rowId}
                        style={
                          isVirtualizing
                            ? {
                                position: "absolute",
                                top: i * rowHeight + "px",
                                left: 0,
                                width: "100%",
                              }
                            : undefined
                        }
                      >
                        <TableRowElem
                          tableConfig={processedTableConfig}
                          id={rowId}
                          data={d}
                          rowIdx={i}
                          isLastRow={i === data.length - 1}
                          // no plus button / ellipsis column after the cells:
                          // the last cell sits on the table's edge
                          lastCellIsTableEdge={!builderProps && !tableConfig.actions}
                          disableRowStripes={disableRowStripes}
                          actions={tableConfig.actions}
                          bulkActions={tableConfig.bulkActions}
                          actionsConfig={actionsConfig}
                          conditionsConfig={conditionsConfig}
                          colWidthConfig={colWidthConfig}
                          handleRowClick={handleRowClick}
                          handleCellChange={handleCellChange}
                          // primitive instead of the selectedRowIds array, so a
                          // selection change only re-renders the rows whose
                          // selected state actually flipped (React.memo holds
                          // for the rest)
                          isRowSelected={bulkActions_selectedRowIds.includes(
                            rowId
                          )}
                          onCheckboxDragStart={handleCheckboxDragStart}
                          onCheckboxDragOver={handleCheckboxDragOver}
                          requestRowDelete={requestRowDelete}
                        />
                      </div>
                    );
                  })}
                </div>

                {emptyStateText && data.length === 0 && (
                  <div
                    style={{
                      padding: "32px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <UI_BODY_SM_DF style={{ color: colors2.grey60 }}>
                      {emptyStateText}
                    </UI_BODY_SM_DF>
                  </div>
                )}

                {/* Stable sentinel below the rows — the lazy-load trigger */}
                {observerRef && (
                  <div
                    ref={observerRef}
                    id={"observerAttached_table_sentinel"}
                    style={{ height: '1px' }}
                  />
                )}

                {isFetchingNextPage && (
                  <TableSkeletonLoader
                    rowsCount={5}
                    dataLength={data?.length || 0}
                  />
                )}
              </>
            )}
          </StyledDataTableRowsContainer>
        </TableWrapper>
      </div>
    </StickyHorizontalScrollbar>
    </Frame>
  );
}

export default TableElem;
